import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { NavLink, useLocation } from "react-router-dom";
import {
  getCurrentProjectGroupElement,
  getSelectedProjectId,
} from "../../../features/stats/state/statsSlice";
import { Tooltip } from "../Tooltip/Tooltip";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { hostedTourAnchors } from "src/features/onboarding/engine/tourViewAnchors";

export type NavigationTabsProps = {
  tabs: {
    title: string;
    urlEndPoint: string;
    hidden?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    preventNavigation?: boolean;
    isActive?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
  }[];
  dataTour?: string;
};

const NavigationTabs = ({ tabs, dataTour }: NavigationTabsProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const selectedProjectId = useAppSelector(getSelectedProjectId);
  const currentProjectGroupElement = useAppSelector(getCurrentProjectGroupElement);
  const activeClassName =
    "inline-block py-2 px-8 text-green-500 rounded-t-lg border-b-2 border-b-green-500 h-full";
  const inStatsView = location.pathname.includes("statistics");
  // We want to hide tabs while on /statistics Overview
  const isStatisticsOverview = selectedProjectId === 0 && inStatsView;
  // We want to disable 'Participants' tab on !need_login projects
  const shouldDisableParticipants =
    inStatsView && currentProjectGroupElement?.project_group.auth_type === "none";

  const isTabDisabled = (tab: NavigationTabsProps["tabs"][number]) => {
    return tab.disabled || (shouldDisableParticipants && tab.urlEndPoint === "learners");
  };

  useTourView(
    "nav-tabs",
    true,
    hostedTourAnchors("nav-tabs", [
      ...(dataTour ? [dataTour] : []),
      ...(isStatisticsOverview
        ? []
        : tabs
            .filter((tab) => !tab.hidden && !isTabDisabled(tab))
            .map((tab) => `tab-${tab.urlEndPoint}`)),
    ]),
  );

  const getDisabledTooltip = (tab: NavigationTabsProps["tabs"][number]) => {
    if (tab.disabled && tab.disabledTooltip) {
      return tab.disabledTooltip;
    }
    if (shouldDisableParticipants && tab.urlEndPoint === "learners") {
      return t("pages.statistics.unavailableForAnonymous");
    }
    return "";
  };
  return (
    <div
      className={`text-sm font-medium text-center text-gray-500 border-b border-gray-200 ${
        isStatisticsOverview ? "mt-8" : ""
      } flex flex-wrap -mb-px
        `}
    >
      <div data-tour={dataTour} className="flex flex-wrap">
        {!isStatisticsOverview &&
          tabs.map((tab, i) => {
            return tab.hidden ? (
              <></>
            ) : tab.preventNavigation ? (
              <Tooltip key={`${tab.urlEndPoint}-${i}`} message={getDisabledTooltip(tab)}>
                <button
                  key={i}
                  type="button"
                  data-tour={isTabDisabled(tab) ? undefined : `tab-${tab.urlEndPoint}`}
                  className={
                    tab.isActive
                      ? activeClassName
                      : `inline-block py-2 px-8 rounded-t-lg border-transparent h-full ${
                          isTabDisabled(tab)
                            ? "text-gray-300 cursor-not-allowed"
                            : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 cursor-pointer"
                        }`
                  }
                  onClick={(e) => {
                    if (isTabDisabled(tab)) {
                      return e.preventDefault();
                    }
                    if (tab?.onClick) {
                      tab.onClick(e);
                    }
                  }}
                >
                  {tab.title}
                </button>
              </Tooltip>
            ) : (
              <Tooltip key={`${tab.urlEndPoint}-${i}`} message={getDisabledTooltip(tab)}>
                <NavLink
                  key={i}
                  to={tab.urlEndPoint}
                  data-tour={isTabDisabled(tab) ? undefined : `tab-${tab.urlEndPoint}`}
                  className={({ isActive }) =>
                    isActive
                      ? activeClassName
                      : `inline-block py-2 px-8 rounded-t-lg border-transparent h-full ${
                          isTabDisabled(tab)
                            ? "text-gray-300 cursor-not-allowed"
                            : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                        }`
                  }
                  onClick={(e) => {
                    if (isTabDisabled(tab)) {
                      return e.preventDefault();
                    }
                    if (tab?.onClick) {
                      tab.onClick(e);
                    }
                  }}
                >
                  {tab.title}
                </NavLink>
              </Tooltip>
            );
          })}
      </div>
    </div>
  );
};

export default NavigationTabs;
